import { useRouteMatch } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { BookingLayout } from './types';
import { PROJECT_BOOKINGS, PROJECT_BOOKINGS_CALENDAR, PROJECT_BOOKINGS_LIST } from '../../core/constants/routes';
import { selectErrorMessage } from '../../core/state/bookings/selectors';
import { GlobalNotificationTypes } from '../../core/enums/GlobalNotificationTypes';
import { spawnGlobalNotification } from '../../core/state/global-notifications/asyncActions';

export const useBookingLayout = (): BookingLayout => {
  const [layout, setLayout] = useState<BookingLayout>(BookingLayout.Calendar);
  const calendarMatch = useRouteMatch([PROJECT_BOOKINGS, PROJECT_BOOKINGS_CALENDAR]);
  const listMatch = useRouteMatch(PROJECT_BOOKINGS_LIST);

  useEffect(() => {
    if (listMatch) {
      setLayout(BookingLayout.List);
    } else if (calendarMatch) {
      setLayout(BookingLayout.Calendar);
    }
  }, [calendarMatch, listMatch]);

  if (!calendarMatch && !listMatch) {
    throw new Error('Unknown route');
  }

  return layout;
};

export const useValidationNotification = (isOpen: boolean, isSubmitted: boolean) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const errorMessage = useSelector(selectErrorMessage); // Error message should be the name of the field that failed validation

  const timeSlotNotification = useMemo(
    () => ({
      type: GlobalNotificationTypes.Error,
      title: t('booking.validation.timeSlot.title'),
      message: t('booking.validation.timeSlot.message')
    }),
    [t]
  );

  const durationNotification = useMemo(
    () => ({
      type: GlobalNotificationTypes.Error,
      title: t('booking.validation.duration.title'),
      message: t('booking.validation.duration.message')
    }),
    [t]
  );
  const operatingHoursNotification = useMemo(
    () => ({
      type: GlobalNotificationTypes.Error,
      title: t('toast.error.title'),
      message: t('projectSettings.blockBookingSlots.form.message.outOfWorkingHours')
    }),
    [t]
  );

  useEffect(() => {
    if (isOpen && isSubmitted && errorMessage) {
      let notification;
      switch (errorMessage) {
        case 'startTime':
          notification = timeSlotNotification;
          break;
        case 'date':
        case 'NotValidDeliveryRange':
          notification = durationNotification;
          break;
        case 'NotValidOperatingHours':
          notification = operatingHoursNotification;
          break;
        default:
          notification = {
            type: GlobalNotificationTypes.Error,
            message: errorMessage
          };
      }
      dispatch(spawnGlobalNotification(notification));
    }
  }, [dispatch, durationNotification, errorMessage, isOpen, isSubmitted, t, timeSlotNotification, operatingHoursNotification]);

  return errorMessage;
};
